import React from "react";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useTranslation } from "react-i18next";

import { MAX_BUYER_EMAIL_TEXT_LENGTH } from "../../../../constants/monetization";

// Le back n'accepte qu'une allowlist de balises et strippe le reste
// silencieusement. La barre d'outils ET la liste des formats sont donc
// limitées exactement à ce qu'il conserve : sans le second, un collage
// depuis Word réintroduirait couleurs, tailles et styles inline.
const TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

const ALLOWED_FORMATS = ["bold", "italic", "underline", "list", "link"];

const MODULES = {
  toolbar: TOOLBAR,
  clipboard: { matchVisual: false },
};

// Quill représente un éditeur vide par ce balisage : il ne doit pas être
// confondu avec un vrai message.
const isQuillEmpty = (html) =>
  !html || html === "<p><br></p>" || html.replace(/<[^>]*>/g, "").trim() === "";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  title: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#1a1f36",
  },
  helper: {
    fontSize: "0.75rem",
    color: "#697386",
  },
  editor: {
    backgroundColor: "white",
    "& .ql-container": {
      minHeight: 140,
      fontSize: "0.9rem",
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
    "& .ql-toolbar": {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  counter: {
    fontSize: "0.75rem",
    color: "#697386",
  },
  counterExceeded: {
    fontSize: "0.75rem",
    color: "#DC2626",
    fontWeight: 600,
  },
  emptyState: {
    fontSize: "0.75rem",
    color: "#697386",
    fontStyle: "italic",
  },
  saveButton: {
    backgroundColor: "#5E77FF",
    color: "white",
    textTransform: "none",
    borderRadius: 8,
    padding: "8px 20px",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#4A5FD1",
    },
  },
}));

const BuyerEmailMessageEditor = ({ value, onChange, onSave, isSaving }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isEmpty = isQuillEmpty(value);
  // La limite porte sur la chaîne envoyée au back, donc balisage compris.
  const length = isEmpty ? 0 : value.length;
  const isTooLong = length > MAX_BUYER_EMAIL_TEXT_LENGTH;

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <MailOutlineIcon style={{ fontSize: 22, color: "#3F51B5" }} />
        <Typography className={classes.title}>
          {t("buyer_email_message.title")}
        </Typography>
      </Box>
      <Typography className={classes.helper}>
        {t("buyer_email_message.helper")}
      </Typography>

      <ReactQuill
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={MODULES}
        formats={ALLOWED_FORMATS}
        placeholder={t("buyer_email_message.placeholder")}
        className={classes.editor}
      />

      <Box className={classes.footer}>
        {isEmpty ? (
          <Typography className={classes.emptyState}>
            {t("buyer_email_message.empty")}
          </Typography>
        ) : (
          <Typography
            className={isTooLong ? classes.counterExceeded : classes.counter}
          >
            {/* Surtout pas "count" : i18next le réserve à la pluralisation et
                irait chercher une clé counter_one / counter_other. */}
            {t("buyer_email_message.counter", {
              current: length,
              max: MAX_BUYER_EMAIL_TEXT_LENGTH,
            })}
          </Typography>
        )}
        <Button
          variant="contained"
          className={classes.saveButton}
          onClick={onSave}
          disabled={isSaving || isTooLong}
        >
          {isSaving ? (
            <CircularProgress size={20} style={{ color: "white" }} />
          ) : (
            t("buyer_email_message.save")
          )}
        </Button>
      </Box>
    </Box>
  );
};

export { isQuillEmpty };
export default BuyerEmailMessageEditor;
